<template>
    <div>

        <!-- ========================================================= -->
        <!-- COMPANY HEADER -->
        <!-- ========================================================= -->
        <div class="bg-white border-bottom">
            <div class="container py-3">
                <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                        <img
                            src="https://getasia.s3.ap-southeast-1.amazonaws.com/images/hextar_logo.png"
                            style="height: 48px; width: auto;"
                        >
                        <div class="border-left ml-3 pl-3">
                            <h5 class="font-weight-bold mb-0">
                                Careers
                            </h5>
                            <small class="text-muted">
                                Find your next opportunity with us
                            </small>
                        </div>
                    </div>
                    <div class="d-none d-md-block">
                        <span class="text-muted">
                            <i class="mdi mdi-briefcase-search-outline mr-1"></i>
                            Career Opportunities
                        </span>
                    </div>

                </div>
            </div>
        </div>

        <!-- ========================================================= -->
        <!-- SEARCH AREA -->
        <!-- ========================================================= -->
        <div class="py-3 bg-info">
            <div class="container">

                <div class="row">

                    <!-- Search -->
                    <div class="col-lg-6 mb-2 mb-lg-0">
                        <b-input-group>
                            <b-input-group-prepend>
                                <b-input-group-text class="bg-white border-0">
                                    <i class="mdi mdi-magnify"></i>
                                </b-input-group-text>
                            </b-input-group-prepend>
                            <b-form-input
                                v-model="filter"
                                class="border-0"
                                placeholder="Search job..."
                                autocomplete="off"
                                v-on:change="searchListing"
                            />
                        </b-input-group>
                    </div>

                    <!-- Company -->
                    <div class="col-lg-3 mb-2 mb-lg-0">
                        <multiselect
                            v-model="companyFilter"
                            track-by="value"
                            label="text"
                            :placeholder="'All Companies'"
                            :select-label="'Select'"
                            :selected-label="'Selected'"
                            :deselect-label="'Select'"
                            :options="companyDdl"
                            :searchable="true"
                            :allow-empty="false"
                            @input="searchListing"
                        />
                    </div>

                    <!-- Department -->
                    <div class="col-lg-3">
                        <multiselect
                            v-model="departmentFilter"
                            track-by="value"
                            label="text"
                            :placeholder="'All Departments'"
                            :select-label="'Select'"
                            :selected-label="'Selected'"
                            :deselect-label="'Select'"
                            :options="departmentDdl"
                            :searchable="true"
                            :allow-empty="false"
                            @input="searchListing"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- ========================================================= -->
        <!-- MAIN -->
        <!-- ========================================================= -->
        <div class="bg-light">
            <div class="container py-4">

                <div class="row">


                    <!-- ================================================= -->
                    <!-- LEFT SIDE -->
                    <!-- ================================================= -->
                    <div class="col-lg-5 mb-4 mb-lg-0">

                        <div class="card shadow-sm">

                            <!-- Result Header -->
                            <div class="card-body border-bottom py-3">
                                <div class="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 class="font-weight-bold mb-1">Available Jobs</h5>
                                        <small class="text-muted">Total: {{ totalPage }}</small>
                                    </div>
                                </div>
                            </div>


                            <!-- ========================================= -->
                            <!-- SCROLLABLE JOB LIST -->
                            <!-- ========================================= -->
                            <div class="job-list-scroll p-3">
                                <template v-if="status.list && !loading">
                                    <div
                                        v-for="(item, index) in tableRows"
                                        :key="index"
                                        :class="[
                                            'card shadow-sm mb-2 job-card cursor-pointer fade-in',
                                            selectedJob?.uuid == item.uuid ? 'border border-secondary shadow-lg' : 'border border-white'
                                        ]"
                                        @click="selectJob(item)"
                                    >
                                        <div class="card-body">
                                            <div class="d-flex align-items-start">
                                                <div class="flex-grow-1 pr-2">
                                                    <h6 class="font-weight-bold mb-2">{{ item.name }}</h6>
                                                    <div
                                                        v-if="item.company"
                                                        class="small text-muted mb-1"
                                                    >
                                                        <i class="mdi mdi-office-building mr-1"></i>
                                                        <CompanyLink :company="item.company" fallback="--" />
                                                    </div>
                                                    <div
                                                        v-if="item.location"
                                                        class="small text-muted mb-1"
                                                    >
                                                        <i class="mdi mdi-map-marker-outline mr-1"></i>
                                                        {{ item.location || '--' }}
                                                    </div>
                                                    <div
                                                        v-if="item.department"
                                                        class="small text-muted mb-2"
                                                    >
                                                        <i class="mdi mdi-account-group-outline mr-1"></i>
                                                        {{ item.department ? item.department.name : '--' }}
                                                    </div>
                                                </div>
                                                <div v-if="item.company?.logoUrl">
                                                    <img
                                                        :src="item.company?.logoUrl"
                                                        class="img-fluid"
                                                        style="width: 80px; height: 80px; object-fit: contain;"
                                                    >
                                                </div>
                                            </div>
                                            <div
                                                v-if="item?.minSalary && item?.maxSalary"
                                                class="mb-2"
                                            >
                                                <b-badge
                                                    variant="light"
                                                    class="border mr-1 px-2 py-2"
                                                >
                                                    <i class="mdi mdi-wallet-outline mr-1"></i>
                                                    <small>{{ salaryRange(item) }}</small>
                                                </b-badge>
                                            </div>
                                            <div
                                                v-if="item.closingDate"
                                                class="small text-muted"
                                            >
                                                <i class="mdi mdi-calendar mr-1"></i>
                                                Closing date: {{ item.closingDate || '--' }}
                                            </div>
                                        </div>
                                    </div>
                                    <b-alert
                                        v-if="tableRows.length == 0"
                                        show
                                        variant="warning"
                                        class="mt-0 text-center"
                                    >
                                        <i class="mdi mdi-alert-circle-outline"></i> {{ $t('message.noResultsFound') }}
                                    </b-alert>
                                </template>
                                <div v-else class="d-flex justify-content-center align-items-center p-5 m-5">
                                    <b-spinner class="m-5" variant="info" role="status"></b-spinner>
                                </div>
                            </div>


                            <!-- ========================================= -->
                            <!-- PAGINATION -->
                            <!-- ========================================= -->
                            <div v-if="totalPage" class="card-footer bg-white">
                                <b-pagination
                                    v-model="currentPage"
                                    :total-rows="totalPage"
                                    :per-page="perPage"
                                    v-on:change="pageNavigation"
                                    size="sm"
                                    align="center"
                                    class="mb-0"
                                />
                            </div>

                        </div>

                    </div>

                    <div class="col-lg-7">
                        <CandidateJobDetail :selectedJob="selectedJob" />
                    </div>

                </div>

            </div>
        </div>
    </div>
</template>
<script>
import Multiselect from "vue-multiselect";
import CandidateJobDetail from "@/views/pages/public/candidateJob/candidateJobDetail";
import i18n from "@/i18n";
import { SESSION, SHARED } from "@/constants";
import { sessionHelper } from "@/utils";
import { mapState } from "vuex";
import { candidateJobMethods } from "@/state/public";
import { lookupMethods } from "@/state/public";
import { CANDIDATE_JOB_MODULE } from "@/state/public/candidateJob.state";

export default {
    components: { Multiselect, CandidateJobDetail },
    async created() {
        await this.lookupCompany({});
        await this.lookupDepartment();

        sessionHelper.clearSearch(SESSION.CANDIDATE_JOB_SEARCH) // clear search whenever page reload
        this.initSearchCriteria();

        this.loading = false;
    },
    data() {
        return {
            filter: "",
            currentPage: 1,
            perPage: SHARED.DEFAULT_PAGE_SIZE,
            companyFilter: null,
            departmentFilter: null,
            searchCriteria: {},
            selectedJobUUID: null,
            defaultLogo: "https://getasia.s3.ap-southeast-1.amazonaws.com/images/hextar_logo.png",
            loading: true,
        };
    },
    methods: {
        ...candidateJobMethods,
        ...lookupMethods,
        initSearchCriteria() {
            let isDefault = true;
            if(sessionHelper.hasSearchCriteria(SESSION.CANDIDATE_JOB_SEARCH)) {
                isDefault = false;
                this.searchCriteria = sessionHelper.retrieveSearch(SESSION.CANDIDATE_JOB_SEARCH);
                this.currentPage = this.searchCriteria.pageNumber;
            }
            if(isDefault) {
                this.searchCriteria = {
                    pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
                    pageSize: SHARED.DEFAULT_PAGE_SIZE,
                    isPublished: 1,
                    isActive: 1,
                    searchValue: "",
                    searchArray: [],
                };
            }
            this.getCandidateJobList(this.searchCriteria);
        },
        getCandidateJobList(filter) {
            const hasSearch = Array.isArray(filter.searchArray) && filter.searchArray.some(item => typeof item === 'string' && item.trim() !== '');
            const hasCompany = filter.companyUuid && filter.companyUuid != 1;
            const hasDepartment = filter.departmentUuid && filter.departmentUuid != 1;

            if (this.$route.query.id && !hasSearch && !hasCompany && !hasDepartment) // ignore query id if search & filters are not empty
                filter.uuid = this.$route.query.id;
            else
                delete filter.uuid;
            
            this.getAllCandidateJob({filter});
            this.searchCriteria = filter;
            sessionHelper.storeSearch(SESSION.CANDIDATE_JOB_SEARCH, filter);
        },
        pageNavigation(pageNumber) {
            let filter = sessionHelper.retrieveSearch(SESSION.CANDIDATE_JOB_SEARCH);
            filter.pageNumber = pageNumber;
            this.getCandidateJobList(filter);
        },
        searchListing() {
            this.searchCriteria.pageNumber = SHARED.DEFAULT_PAGE_NUMBER;
            this.searchCriteria.searchArray = [];
            this.searchCriteria.searchArray.push(this.filter);
            this.searchCriteria.isPublished = 1;
            this.searchCriteria.isActive = 1;
            if (this.filter == '' || this.filter == null) {
                this.searchCriteria.pageNumber = 1;
                this.currentPage = 1;
            }

            if (this.companyFilter?.value == 1)
                delete this.searchCriteria.companyUuid;
            else if (this.companyFilter?.value)
                this.searchCriteria.companyUuid = this.companyFilter.value;

            if (this.departmentFilter?.value == 1)
                delete this.searchCriteria.departmentUuid;
            else if (this.departmentFilter?.value)
                this.searchCriteria.departmentUuid = this.departmentFilter.value;

            this.getCandidateJobList(this.searchCriteria);
        },
        selectJob(data) {
            this.selectedJobUUID = data.uuid;

            if (window.innerWidth < 992) {
                this.$nextTick(() => {
                    this.$bvModal.show('job-detail-modal');
                });
            }
        },
        salaryRange(data) {
            if (data?.minSalary && data?.maxSalary) return `${data.minSalary} - ${data.maxSalary}`;
            if (data?.minSalary) return data.minSalary;
            if (data?.maxSalary) return data.maxSalary;
            return '--';
        },
    },
    computed: {
        ...mapState(CANDIDATE_JOB_MODULE, ['status']),
        companyDdl() {
            return [
                { value: 1, text: i18n.t('label.all') },
                ...this.$store.state.public.lookup.companyDdl,
            ];
        },
        departmentDdl() {
            return [
                { value: 1, text: i18n.t('label.all') },
                ...this.$store.state.public.lookup.departmentDdl,
            ];
        },
        tableRows() {
            return this.$store.state.public.candidateJob.list.data || [];
        },
        totalPage() {
            return this.$store.state.public.candidateJob.list.pagination.total || 0;
        },
        selectedJob() {
            return this.tableRows.find((item) => item.uuid == this.selectedJobUUID) || this.tableRows[0] || null;
        },
    },
}
</script>
<style scoped>
    .bg-info, .btn-info {
        background-color:#1AB0E6 !important;
    }

    .job-list-scroll {
        height: calc(100vh - 360px);
        min-height: 500px;
        overflow-y: auto;
    }

    .job-card {
        transition: box-shadow 0.2s ease-in-out, border-color 0.15s ease-in-out;
    }

    .job-card:hover {
        border-color: #007bff;
        box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.08) !important;
    }

    @media (max-width: 991.98px) {
        .job-list-scroll {
            height: auto;
            min-height: 0;
            overflow-y: visible;
        }
    }
</style>
